import { IRetryPolicySettings, WebSocketServerConnection, IServerConnectionLogger, TimeoutConfigCallback, IServerConnection, IServer } from '@/app-modules/server-communications';
import { IChatServerService, IGameServerService, JsonGameServerService, ChatServerService, GameServerService, IGameServerServiceConfiguration, IJsonGameServerService } from '@/app-modules/server-service/server-service';
import { injectable, inject } from 'inversify';
import { IConfigurationService, ConfigurationPaths, IChatModsConfiguration, ConfigurationKey } from '@/app-modules/config';
import { ChatServerResponseStreams } from '@/components/chat/_domain';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { Semaphore } from '@/app-modules/semaphore';
import { ConnectionTimeoutOverride } from '@/app-modules/store/modules/user-preferences';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import {IConnectionTimeout, IWebsocketConnectionOptions, ServerMessageType } from '../server-communications/types';

const CM_CHAT_GAME_NAME = 'cmchat';
const CS_CHAT_GAME_NAME = 'cschat';
const ARCADE_CHAT_GAME_NAME = 'arcade';
const DEFAULT_TIMEOUT = 20000;
const MAX_TIMEOUT = 120000;
const TIMEOUT_SCALE_FACTOR = 1.5;

interface IServerServiceFactory {
  createChatService(gameName?: string, chatRoomName?: string, isArcade?: boolean): Promise<IChatServerService>;
  createGameService(configuration: IGameServerServiceConfiguration): IGameServerService;
  createJsonGameService(configuration: IGameServerServiceConfiguration): IJsonGameServerService;
  createGameServerConnection(gameServerUrl: string, gameName: string): IServerConnection;
}

@injectable()
class ServerServiceFactory implements IServerServiceFactory {
  private cmChatServerService: ChatServerService;
  private readonly cmChatServerServiceSemaphore = new Semaphore(1);
  private csChatServerService: ChatServerService;
  private readonly csChatServerServiceSemaphore = new Semaphore(1);
  private arcadeChatServerService: ChatServerService;
  private readonly arcadeChatServerServiceSemaphore = new Semaphore(1);

  constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerConnectionLogger) private readonly connectionLogger: IServerConnectionLogger,
    @inject(SERVICE_IDENTIFIERS.IEventBusService) private readonly eventBusService: IEventBusService
  ) {}

  public async createChatService(gameName: string = CM_CHAT_GAME_NAME, chatRoomName: string = '', isArcade: boolean = false): Promise<IChatServerService> {
    if (gameName === CM_CHAT_GAME_NAME) {
      return await this.createChatServerServiceForCmChat();
    }

    if (gameName === CS_CHAT_GAME_NAME) {
      return await this.createChatServerServiceForCsChat();
    }

    if (gameName === ARCADE_CHAT_GAME_NAME && !chatRoomName) {
      return await this.createChatServerServiceForArcade();
    }

    return await this.createChatServerService(gameName, chatRoomName, isArcade);
  }

  public createGameService(configuration: IGameServerServiceConfiguration): IGameServerService {
    return new GameServerService(configuration);
  }

  public createJsonGameService(configuration: IGameServerServiceConfiguration): IJsonGameServerService {
    return new JsonGameServerService(configuration);
  }

  public createGameServerConnection(gameServerUrl: string, gameName: string): IServerConnection {
    const defaultKeepAliveTimeout = Number(this.configService.getConfigurationItem(ConfigurationKey.GAME_WEBSOCKET_KEEP_ALIVE_TIMEOUT_MILLISECONDS));
    const defaultConnectionLostTimeout = Number(this.configService.getConfigurationItem(ConfigurationKey.GAME_WEBSOCKET_CONNECTION_LOST_DETECTION_TIMEOUT_MILLISECONDS));

    const options: IWebsocketConnectionOptions = {
      serverUrl: gameServerUrl,
      format: 'xml',
      partialMessages: true,
      connectionTimeout: null,
      connectionLogger: this.connectionLogger,
      eventBusService: this.eventBusService,
      enableKeepAlive: false
    };

    if (gameName === 'Britains Biggest Bingo'){
      options.format = 'json';
      options.partialMessages = false;
    }
    return this.createWebSocketServerConnection(options, defaultKeepAliveTimeout, defaultConnectionLostTimeout);
  }

  private async createChatServerServiceForCmChat(): Promise<ChatServerService> {
    const releaseSemaphoreLock = await this.cmChatServerServiceSemaphore.acquire();
    if (!this.cmChatServerService) {
      const chatRoomName: string = null;
      this.cmChatServerService = await this.createChatServerService(CM_CHAT_GAME_NAME, chatRoomName);
    }
    releaseSemaphoreLock();

    return this.cmChatServerService;
  }

  private async createChatServerServiceForCsChat(): Promise<ChatServerService> {
    const releaseSemaphoreLock = await this.csChatServerServiceSemaphore.acquire();
    if (!this.csChatServerService) {
      const chatRoomName: string = null;
      this.csChatServerService = await this.createChatServerService(CM_CHAT_GAME_NAME, chatRoomName);
    }
    releaseSemaphoreLock();

    return this.csChatServerService;
  }

  private async createChatServerServiceForArcade(): Promise<ChatServerService> {
    const releaseSemaphoreLock = await this.arcadeChatServerServiceSemaphore.acquire();
    if (!this.arcadeChatServerService) {
      const chatRoomName: string = null;
      const isArcade = true;
      this.arcadeChatServerService = await this.createChatServerService(ARCADE_CHAT_GAME_NAME, chatRoomName, isArcade);
    }
    releaseSemaphoreLock();

    return this.arcadeChatServerService;
  }

  private async createChatServerService(gameName: string, chatRoomName: string, isArcade: boolean = false): Promise<ChatServerService> {
    const config = await this.configService.getConfiguration();
    const connection = this.getChatServerConnection(config, gameName, chatRoomName, isArcade);
    const createStreams = ChatServerResponseStreams.createChatStreams;
    const retryPolicySettings = this.getChatRetryPolicySettings();
    return new ChatServerService({connection, createStreams, retryPolicySettings});
  }

  private getChatServerConnection(config: IChatModsConfiguration, gameName: string, chatRoomName: string, isArcade: boolean) {
    const chatServerUrl = this.getChatWebSocketServerUrl(config, gameName, chatRoomName, isArcade);
    const defaultKeepAliveTimeout = Number(this.configService.getConfigurationItem(ConfigurationKey.CHAT_WEBSOCKET_KEEP_ALIVE_TIMEOUT_MILLISECONDS));
    const defaultConnectionLostTimeout = Number(this.configService.getConfigurationItem(ConfigurationKey.CHAT_WEBSOCKET_CONNECTION_LOST_DETECTION_TIMEOUT_MILLISECONDS));

    const options: IWebsocketConnectionOptions = {
      serverUrl: chatServerUrl,
      format: 'xml',
      partialMessages: true,
      connectionTimeout: null,
      connectionLogger: this.connectionLogger,
      eventBusService: this.eventBusService,
      enableKeepAlive: true
    };

    return this.createWebSocketServerConnection(options, defaultKeepAliveTimeout, defaultConnectionLostTimeout);
  }

  private getChatWebSocketServerUrl(config: IChatModsConfiguration, gameName: string, chatRoomName: string, isArcade: boolean): string {
    if (isArcade) {
      return this.getChatWebSocketServerUrlForArcade(chatRoomName);
    }
    return this.getChatWebSocketServerUrlForBingo(config, gameName, chatRoomName);
  }

  private getChatWebSocketServerUrlForBingo(config: IChatModsConfiguration, gameName: string, chatRoomName: string): string {
    if (gameName === 'Britains Biggest Bingo'){
      return this.getChatWebSocketServerUrlForBingoNewFormat(config, gameName, chatRoomName);
    }
    const websocketUrl = this.configService.getConfigurationItem(ConfigurationKey.CHAT_WEBSOCKET_SERVER_URL);
    const serverPath = this.getChatWebSocketServerPath(config, gameName, chatRoomName);
    return `${websocketUrl}${serverPath}`;
  }

  private getChatWebSocketServerUrlForBingoNewFormat(config: IChatModsConfiguration, gameNameWithSpaces: string, chatRoomName: string): string {
    const websocketUrl = this.configService.getConfigurationItem(ConfigurationKey.CHAT_NEW_WEBSOCKET_SERVER_URL);
    const gamename = `-${this.getChatWebSocketServerPath(config, gameNameWithSpaces, '')}`;

    const urlPostfix = this.getNewBingoStyleChatWebSocketServerPostfix(config, gameNameWithSpaces, chatRoomName);
    return `${websocketUrl.format({gamename})}${urlPostfix}`;
  }

  private getChatWebSocketServerUrlForArcade(chatRoomName: string): string {
    const roomname = `-${(chatRoomName || 'silent').toLowerCase().replace(' ', '')}`;
    const websocketUrl = this.configService.getConfigurationItem(ConfigurationKey.ARCADE_CHAT_WEBSOCKET_SERVER_URL);
    return websocketUrl.format({ roomname });
  }

  private getNewBingoStyleChatWebSocketServerPostfix(config: IChatModsConfiguration, gameName: string, chatRoomName: string): string {
    gameName = gameName.replace(/\s/g, '').toLowerCase();

    if (chatRoomName) {
      chatRoomName = '_' + (chatRoomName.replace(/[^0-9a-zA-Z]+/g, '').toLowerCase());
    }

    const chatRoomConfigPath = ConfigurationPaths.forChatServerUrl(gameName + chatRoomName);
    const gameNameConfigPath = ConfigurationPaths.forChatServerUrl(gameName);
    return config.getConfigurationByPath(chatRoomConfigPath) || config.getConfigurationByPath(gameNameConfigPath) || gameName;
  }
  private getChatWebSocketServerPath(config: IChatModsConfiguration, gameName: string, chatRoomName: string): string {
    gameName = gameName.replace(/\s/g, '').toLowerCase();

    if (chatRoomName) {
      chatRoomName = '_' + (chatRoomName.replace(' ', '').toLowerCase());
    }

    const chatRoomConfigPath = ConfigurationPaths.forChatServerUrl(gameName + chatRoomName);
    const gameNameConfigPath = ConfigurationPaths.forChatServerUrl(gameName);
    return config.getConfigurationByPath(chatRoomConfigPath) || config.getConfigurationByPath(gameNameConfigPath) || `${gameName}`;
  }

  private getChatRetryPolicySettings(): IRetryPolicySettings {
    return {
      enabled: true,
      maxRetries: 5
    };
  }

  private createWebSocketServerConnection(options: IWebsocketConnectionOptions, defaultKeepAliveTimeout: number, defaultConnectionLostTimeout: number): IServerConnection {
    const getOverrideTimeout = (overrides?: ConnectionTimeoutOverride): number | undefined => {
      return overrides ? (overrides.webSocketConnectionTimeoutInSeconds * 1000) : undefined;
    };
    const keepAliveTimeoutCallback: TimeoutConfigCallback = () => defaultKeepAliveTimeout || DEFAULT_TIMEOUT;
    const connectionLostTimeoutCallback: TimeoutConfigCallback = (connection, retryInfo) => {
      const overrides = this.configService.getWebSocketOverrideBehaviour();
      let timeout = getOverrideTimeout(overrides) || defaultConnectionLostTimeout || DEFAULT_TIMEOUT;
      if (overrides && overrides.useAutomaticBackoff && retryInfo && retryInfo.retryAttempt > 0) {
        timeout = timeout * Math.pow(TIMEOUT_SCALE_FACTOR, retryInfo.retryAttempt);
        if (timeout > MAX_TIMEOUT) {
          timeout = MAX_TIMEOUT;
        }
      }
      return timeout;
    };

    options.keepAliveConfigCallback = keepAliveTimeoutCallback;
    options.connectionLostConfigCallback = connectionLostTimeoutCallback;

    return new WebSocketServerConnection(options);
  }
}

export { IServerServiceFactory, ServerServiceFactory };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/server-service/service-factory.ts